
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'research', name: 'Research' },
    { id: 'technology', name: 'Technology' },
    { id: 'health', name: 'Health Tips' },
    { id: 'industry', name: 'Industry News' },
  ];

  const featuredPost = {
    image: 'https://public.readdy.ai/ai/img_res/df0e68d8712024227bfd555f3d24745d.jpg',
    category: 'Research',
    title: 'Breakthrough in Molecular Diagnostics: The Future of Personalized Medicine',
    excerpt: 'Discover how advanced molecular diagnostic techniques are revolutionizing personalized medicine and improving patient outcomes through precision testing and targeted treatments.',
    author: 'Dr. Sarah Johnson',
    date: 'January 15, 2024',
    readTime: '8 min read',
  };

  const posts = [
    {
      id: 1,
      category: 'technology',
      image: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20medical%20laboratory%20AI%20technology%20healthcare%20diagnostics%20modern%20equipment%20clean%20professional%20high%20quality&width=600&height=400&seq=blog1&orientation=landscape',
      title: 'AI-Powered Diagnostics: Transforming Laboratory Medicine',
      excerpt: 'Explore how artificial intelligence is enhancing diagnostic accuracy and efficiency in modern laboratories.',
      author: 'Dr. Michael Chen',
      date: 'January 12, 2024',
      readTime: '6 min read',
    },
    {
      id: 2,
      category: 'health',
      image: 'https://readdy.ai/api/search-image?query=preventive%20healthcare%20blood%20testing%20medical%20checkup%20clean%20professional%20laboratory%20environment%20high%20quality%20wellness&width=600&height=400&seq=blog2&orientation=landscape',
      title: 'The Importance of Regular Health Screenings',
      excerpt: 'Learn why routine laboratory tests are crucial for early disease detection and maintaining optimal health.',
      author: 'Dr. Emily Rodriguez',
      date: 'January 10, 2024',
      readTime: '5 min read',
    },
    {
      id: 3,
      category: 'research',
      image: 'https://readdy.ai/api/search-image?query=genetic%20testing%20DNA%20research%20laboratory%20modern%20molecular%20diagnostics%20clean%20professional%20scientific%20environment%20high%20quality&width=600&height=400&seq=blog3&orientation=landscape',
      title: 'Genetic Testing: Understanding Your DNA',
      excerpt: 'A comprehensive guide to genetic testing, its applications, and how it can inform your healthcare decisions.',
      author: 'Dr. Robert Anderson',
      date: 'January 8, 2024',
      readTime: '7 min read',
    },
    {
      id: 4,
      category: 'industry',
      image: 'https://readdy.ai/api/search-image?query=medical%20laboratory%20quality%20standards%20certification%20ISO%20compliance%20clean%20professional%20healthcare%20facility%20high%20quality&width=600&height=400&seq=blog4&orientation=landscape',
      title: 'Quality Standards in Medical Laboratories',
      excerpt: 'Understanding the importance of ISO certification and quality control in laboratory operations.',
      author: 'Dr. Jennifer Martinez',
      date: 'January 5, 2024',
      readTime: '6 min read',
    },
    {
      id: 5,
      category: 'technology',
      image: 'https://readdy.ai/api/search-image?query=point%20of%20care%20testing%20rapid%20diagnostic%20devices%20modern%20medical%20technology%20clean%20professional%20healthcare%20high%20quality&width=600&height=400&seq=blog5&orientation=landscape',
      title: 'Point-of-Care Testing: Bringing Labs to Patients',
      excerpt: 'How portable diagnostic devices are making healthcare more accessible and efficient.',
      author: 'Dr. Michael Chen',
      date: 'January 3, 2024',
      readTime: '5 min read',
    },
    {
      id: 6,
      category: 'health',
      image: 'https://readdy.ai/api/search-image?query=blood%20work%20interpretation%20medical%20test%20results%20healthcare%20consultation%20clean%20professional%20laboratory%20high%20quality&width=600&height=400&seq=blog6&orientation=landscape',
      title: 'Understanding Your Blood Test Results',
      excerpt: 'A patient-friendly guide to interpreting common blood test parameters and what they mean for your health.',
      author: 'Dr. Sarah Johnson',
      date: 'December 28, 2023',
      readTime: '8 min read',
    },
  ];

  const popularPosts = [
    { title: 'Top 10 Laboratory Tests Everyone Should Know', views: '12.5K' },
    { title: 'COVID-19 Testing: What You Need to Know', views: '10.2K' },
    { title: 'Advances in Cancer Diagnostics', views: '9.8K' },
  ];

  const filteredPosts = posts.filter(post => 
    (selectedCategory === 'all' || post.category === selectedCategory) &&
    (searchQuery === '' || post.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=medical%20laboratory%20blog%20knowledge%20sharing%20healthcare%20education%20modern%20professional%20environment%20high%20quality&width=1920&height=800&seq=blog-hero&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Our Blog</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Insights, research, and updates from the world of laboratory medicine
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Blog</span>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <AnimatedSection className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="relative h-96 lg:h-auto overflow-hidden">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-6 left-6">
                  <span className="px-4 py-2 bg-primary-500 text-white rounded-full text-sm font-semibold">
                    Featured
                  </span>
                </div>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold mb-4 w-fit">
                  {featuredPost.category}
                </span>
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-gray-900 mb-4">
                  {featuredPost.title}
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
                  <span>{featuredPost.author}</span>
                  <span>•</span>
                  <span>{featuredPost.date}</span>
                  <span>•</span>
                  <span>{featuredPost.readTime}</span>
                </div>
                <Link
                  to="#"
                  className="inline-flex items-center space-x-2 text-primary-600 font-semibold hover:text-royal-600 transition-colors duration-300"
                >
                  <span>Read Full Article</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Blog Grid */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-6 sticky top-24">
                {/* Search */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-display font-bold text-gray-900 mb-4">Search</h3>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Categories */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-display font-bold text-gray-900 mb-4">Categories</h3>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-300 cursor-pointer ${
                          selectedCategory === category.id
                            ? 'bg-primary-500 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Popular Posts */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-display font-bold text-gray-900 mb-4">Popular Posts</h3>
                  <div className="space-y-4">
                    {popularPosts.map((post, index) => (
                      <Link
                        key={index}
                        to="#"
                        className="block group"
                      >
                        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-300 mb-1">
                          {post.title}
                        </h4>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <i className="ri-eye-line"></i>
                          <span>{post.views} views</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Posts Grid */}
            <div className="lg:col-span-3">
              <div className="mb-8">
                <h2 className="text-2xl font-display font-bold text-gray-900">
                  {filteredPosts.length} Article{filteredPosts.length !== 1 ? 's' : ''} Found
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {filteredPosts.map((post, index) => (
                  <AnimatedSection key={post.id} delay={index * 0.1}>
                    <article className="group bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-primary-600 rounded-full text-sm font-semibold capitalize">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center space-x-3 text-xs text-gray-500 mb-3">
                          <span>{post.author}</span>
                          <span>•</span>
                          <span>{post.date}</span>
                        </div>
                        <h3 className="text-xl font-display font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">{post.readTime}</span>
                          <Link
                            to="#"
                            className="inline-flex items-center space-x-2 text-primary-600 font-semibold hover:text-royal-600 transition-colors duration-300"
                          >
                            <span>Read More</span>
                            <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                          </Link>
                        </div>
                      </div>
                    </article>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <AnimatedSection className="py-24 bg-gradient-to-r from-primary-600 to-royal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Get the latest insights and updates delivered directly to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-6 py-4 rounded-lg focus:outline-none text-sm"
            />
            <button className="px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-cyan-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default BlogPage;
